'use client'

import * as React from 'react'
import { ChevronRightIcon } from 'lucide-react'

import {
  Checkbox,
  FILTER_CHECKBOX_CLASS,
  FILTER_CHECKBOX_ICON_CLASS,
} from '@/components/ui/checkbox'
import {
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { onActivateKey } from '@/lib/utils/activate-key'

/**
 * Rows inside a provider default to `inline` (accordion) instead of a side flyout submenu.
 * The mobile widget menu sets it: at phone width every level of flyout opens further to the
 * side and runs off-screen (WidgetMenuDropdownItem).
 */
export const FilterRowInlineContext = React.createContext(false)

export interface FilterRowProps {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
  label: string
  valueLabel?: string
  valueTooltip?: string // full text shown in tooltip when valueLabel is truncated
  children?: React.ReactNode // submenu content
  className?: string
  labelClassName?: string
  valueClassName?: string
  contentClassName?: string // for SubContent
  svgClassName?: string
  inline?: boolean // render children as inline accordion instead of DropdownMenuSub
  defaultExpanded?: boolean // inline accordion only: start open
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function FilterRow({
  checked,
  onCheckedChange,
  disabled,
  label,
  valueLabel,
  valueTooltip,
  children,
  className,
  labelClassName,
  valueClassName,
  contentClassName,
  svgClassName,
  inline,
  defaultExpanded = false,
  open,
  onOpenChange,
}: FilterRowProps) {
  const inlineByContext = React.useContext(FilterRowInlineContext)
  const isInline = inline ?? inlineByContext
  const hasCheckbox = checked !== undefined && onCheckedChange !== undefined
  const hasChildren = React.Children.count(children) > 0
  const [expanded, setExpanded] = React.useState(defaultExpanded)

  const row = (
    <div
      className={cn(
        'flex h-10 w-full items-center gap-3 px-3',
        hasCheckbox && 'focus-row',
        'transition-colors',
        hasCheckbox && !disabled && 'cursor-pointer',
        hasCheckbox && !hasChildren && !disabled && 'hover:bg-foreground/5',
        disabled && 'opacity-40',
        className
      )}
      onClick={() => {
        if (!hasCheckbox || disabled) return
        onCheckedChange(!checked)
      }}
    >
      {hasCheckbox && (
        <Checkbox
          className={cn(FILTER_CHECKBOX_CLASS, 'focus-row-target')}
          iconClassName={FILTER_CHECKBOX_ICON_CLASS}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          // In `inline` mode this checkbox sits inside a parent `role="button"` that
          // also toggles expand/collapse on Space/Enter (see below). Space on this
          // checkbox's own native button behavior fires a keydown that bubbles to that
          // parent handler unless stopped here — without this, checking a box also
          // collapses/expands the accordion in the same keystroke.
          onKeyDown={(e) => e.stopPropagation()}
        />
      )}
      <span
        className={cn(
          'flex-1 text-left text-sm tracking-tight',
          !hasCheckbox && 'ml-0',
          labelClassName
        )}
      >
        {label}
      </span>
      {valueLabel &&
        (valueTooltip ? (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'text-foreground/50 ml-auto max-w-30 cursor-default truncate text-right text-xs tracking-tight',
                    valueClassName
                  )}
                >
                  {valueLabel}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-48 text-xs">
                {valueTooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span
            className={cn(
              'text-foreground/50 ml-auto max-w-30 truncate text-right text-xs tracking-tight',
              valueClassName
            )}
          >
            {valueLabel}
          </span>
        ))}
    </div>
  )

  if (!hasChildren) {
    return row
  }

  if (isInline) {
    return (
      <div className="flex flex-col">
        <div
          role="button"
          tabIndex={0}
          className={cn(
            'hover:bg-foreground/5 flex h-10 w-full cursor-pointer items-center pr-2 text-sm outline-none',
            expanded && 'bg-foreground/5',
            className
          )}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={onActivateKey(() => setExpanded((v) => !v))}
        >
          {row}
          <ChevronRightIcon
            className={cn(
              'text-foreground/40 mr-1 ml-auto size-3.5 shrink-0 transition-transform',
              expanded && 'rotate-90'
            )}
          />
        </div>
        {expanded && (
          <div className={cn('max-h-[min(500px,45dvh)] overflow-y-auto pl-3', contentClassName)}>
            {children}
          </div>
        )}
      </div>
    )
  }

  return (
    <DropdownMenuSub open={open} onOpenChange={onOpenChange}>
      <DropdownMenuSubTrigger
        svgClassName={svgClassName}
        className={cn(
          'hover:bg-foreground/5 data-[state=open]:bg-foreground/10 flex h-10 w-full cursor-pointer items-center p-0 pr-2 text-sm outline-none focus:bg-transparent',
          className
        )}
      >
        {row}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          className={cn(
            'z-200 max-h-[min(500px,45dvh)] min-w-36 overflow-y-auto p-1',
            contentClassName
          )}
        >
          {children}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}
